import React, { useState, useEffect } from 'react';
import { getOrganizationLeads, getTeamsByOrganization } from '../../api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DateRangeFilter from '../common/DateRangeFilter';

function OrganizationDashboard({ user, organizationId, onNavigateToTeamDashboard }) {
  // Date Range State - Standard: Letzte 14 Tage
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 14);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      days: 14
    };
  });

  const [orgStats, setOrgStats] = useState({
    totalLeads: 0,
    qualified: 0,
    noInterest: 0,
    revoked: 0,
    notQualified: 0,
    open: 0,
    kwhTooLow: 0,
    monumentProtection: 0,
    roofNeedsRenovation: 0,
    technicallyNotPossible: 0,
    duplicate: 0,
    loading: true,
    error: null
  });

  const [chartData, setChartData] = useState({
    topPerformers: [],
    dailyLeads: [],
    loading: true
  });

  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null); // Für Hover-Effekt
  
  // Pagination State für Team Performance
  const [teamIndex, setTeamIndex] = useState(0);
  const teamsPerPage = 6;
  
  const isOrgLeader = user?.isOrgLeader || false;
  const isAdmin = user?.isAdmin || false;

  // Verwende organizationId aus Props
  const currentOrganizationId = organizationId;

  // Pagination Funktionen für Team Performance
  const getCurrentPageTeams = () => {
    const startIndex = teamIndex * teamsPerPage;
    const endIndex = startIndex + teamsPerPage;
    return chartData.topPerformers.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const totalPages = Math.ceil(chartData.topPerformers.length / teamsPerPage);
    return Math.max(1, totalPages); // Mindestens 1 Seite
  };

  const handlePreviousPage = () => {
    setTeamIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    setTeamIndex(prev => Math.min(totalPages - 1, prev + 1));
  };

  // Reset pagination index when data changes
  useEffect(() => {
    setTeamIndex(0);
  }, [chartData.topPerformers.length]);

  // Ensure teamIndex doesn't exceed available pages
  useEffect(() => {
    const totalPages = getTotalPages();
    if (teamIndex >= totalPages) {
      setTeamIndex(Math.max(0, totalPages - 1));
    }
  }, [teamIndex, chartData.topPerformers.length]);

  // Lead-Statistiken für Organisationsleiter laden
  useEffect(() => {
    const loadOrganizationStatistics = async () => {
      if (!isOrgLeader && !isAdmin) {
        setOrgStats(prev => ({ ...prev, loading: false }));
        setChartData(prev => ({ ...prev, loading: false }));
        return;
      }

      if (!currentOrganizationId) {
        setOrgStats(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Keine Organisations-ID gefunden' 
        }));
        setChartData(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        setOrgStats(prev => ({ ...prev, loading: true, error: null }));
        setChartData(prev => ({ ...prev, loading: true }));
        
        // Teams der Organisation laden
        const teamsResponse = await getTeamsByOrganization(user.id, currentOrganizationId);
        if (teamsResponse.success) {
          setTeams(teamsResponse.teams || []);
        }
        
        // Alle Leads der Organisation laden
        const response = await getOrganizationLeads(user.id, currentOrganizationId);
        
        if (response.success) {
          const leads = response.leads || [];
          
          // Alle Leads der Organisation verwenden (ohne zeitliche Filterung)
          const allLeads = leads;
          
          // Filter Leads nach Datum
          const filteredLeads = allLeads.filter(lead => {
            const leadDate = new Date(lead.erstellt);
            const startDate = new Date(dateRange.startDate);
            const endDate = new Date(dateRange.endDate);
            endDate.setHours(23, 59, 59, 999); // Ende des Tages
            return leadDate >= startDate && leadDate <= endDate;
          });
          
          // Statistiken berechnen
          const stats = {
            totalLeads: filteredLeads.length,
            qualified: 0,
            noInterest: 0,
            revoked: 0,
            notQualified: 0,
            open: 0,
            kwhTooLow: 0,
            monumentProtection: 0,
            roofNeedsRenovation: 0,
            technicallyNotPossible: 0,
            duplicate: 0,
            loading: false,
            error: null
          };
          
          filteredLeads.forEach(lead => {
            // PV-Status prüfen
            if (lead.status_pv) {
              const pvStatus = lead.status_pv.toLowerCase();
              if (pvStatus.includes('qualifiziert')) {
                stats.qualified++;
              } else if (pvStatus.includes('kein interesse')) {
                stats.noInterest++;
              } else if (pvStatus.includes('widerruf')) {
                stats.revoked++;
              } else if (pvStatus.includes('test lead') || pvStatus.includes('telefonnummer ungültig') || lead.status_pv_invalid) {
                stats.notQualified++;
              } else if (pvStatus.includes('offen')) {
                stats.open++;
              } else if (pvStatus.includes('kwh zu niedrig')) {
                stats.kwhTooLow++;
              } else if (pvStatus.includes('denkmalschutz')) {
                stats.monumentProtection++;
              } else if (pvStatus.includes('dach muss saniert werden')) {
                stats.roofNeedsRenovation++;
              } else if (pvStatus.includes('technisch nicht möglich')) {
                stats.technicallyNotPossible++;
              } else if (pvStatus.includes('dublette')) {
                stats.duplicate++;
              }
            }
            
            // WP-Status prüfen
            if (lead.status_wp) {
              const wpStatus = lead.status_wp.toLowerCase();
              if (wpStatus.includes('qualifiziert')) {
                stats.qualified++;
              } else if (wpStatus.includes('kein interesse')) {
                stats.noInterest++;
              } else if (wpStatus.includes('widerruf')) {
                stats.revoked++;
              } else if (wpStatus.includes('test lead') || wpStatus.includes('telefonnummer ungültig') || lead.status_wp_invalid) {
                stats.notQualified++;
              } else if (wpStatus.includes('offen')) {
                stats.open++;
              } else if (wpStatus.includes('kwh zu niedrig')) {
                stats.kwhTooLow++;
              } else if (wpStatus.includes('denkmalschutz')) {
                stats.monumentProtection++;
              } else if (wpStatus.includes('dach muss saniert werden')) {
                stats.roofNeedsRenovation++;
              } else if (wpStatus.includes('technisch nicht möglich')) {
                stats.technicallyNotPossible++;
              } else if (wpStatus.includes('dublette')) {
                stats.duplicate++;
              }
            }
          });
          
          setOrgStats(stats);

          // Chart-Daten verarbeiten
          processChartData(filteredLeads, teamsResponse.teams || []);
          
        } else {
          setOrgStats(prev => ({
            ...prev,
            loading: false,
            error: response.error || 'Fehler beim Laden der Organisations-Statistiken'
          }));
          setChartData(prev => ({ ...prev, loading: false }));
        }
      } catch (err) {
        setOrgStats(prev => ({
          ...prev,
          loading: false,
          error: 'Netzwerkfehler: ' + err.message
        }));
        setChartData(prev => ({ ...prev, loading: false }));
      }
    };

    const processChartData = (allLeads, teamsList) => {
      // Top Performer Daten generieren (basierend auf Teams)
      const teamLeadsMap = {};
      
      // Teams initialisieren
      teamsList.forEach(team => {
        teamLeadsMap[team.id] = {
          id: team.id,
          name: team.name,
          total: 0,
          qualified: 0
        };
      });
      
      allLeads.forEach(lead => {
        const teamId = lead.team_id;
        
        if (teamId && teamLeadsMap[teamId]) {
          teamLeadsMap[teamId].total++;
          
          // Qualifizierte zählen
          let isQualified = false;
          if (lead.status_pv && lead.status_pv.toLowerCase().includes('qualifiziert')) {
            isQualified = true;
          }
          if (lead.status_wp && lead.status_wp.toLowerCase().includes('qualifiziert')) {
            isQualified = true;
          }
          
          if (isQualified) {
            teamLeadsMap[teamId].qualified++;
          }
        }
      });

      // Alle Teams sortieren (auch Teams ohne Leads)
      const allTeams = Object.values(teamLeadsMap)
        .map(team => ({
          id: team.id, // Team ID hinzufügen für Navigation
          name: team.name.length > 12 ? team.name.substring(0, 12) + '...' : team.name,
          fullName: team.name,
          Gesamt: team.total,
          Qualifiziert: team.qualified
        }))
        .sort((a, b) => {
          // Sortierung: Erst nach Qualifizierten, dann nach Gesamt, dann alphabetisch
          if (b.Qualifiziert !== a.Qualifiziert) {
            return b.Qualifiziert - a.Qualifiziert;
          }
          if (b.Gesamt !== a.Gesamt) {
            return b.Gesamt - a.Gesamt;
          }
          return a.fullName.localeCompare(b.fullName);
        });

      // Daily Leads basierend auf dem ausgewählten Zeitraum
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      const dailyLeadsMap = {};
      
      // Alle Tage im ausgewählten Zeitraum initialisieren
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = currentDate.toLocaleDateString('de-DE', { 
          day: '2-digit', 
          month: '2-digit'
        });
        dailyLeadsMap[dateStr] = 0;
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Leads zu den entsprechenden Tagen hinzufügen
      allLeads.forEach(lead => {
        const leadDate = new Date(lead.erstellt);
        if (leadDate >= startDate && leadDate <= endDate) {
          const dateStr = leadDate.toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit'
          });
          if (dailyLeadsMap.hasOwnProperty(dateStr)) {
            dailyLeadsMap[dateStr]++;
          }
        }
      });

      const dailyLeads = Object.entries(dailyLeadsMap).map(([date, count]) => ({
        date,
        leads: count
      }));

      setChartData({
        topPerformers: allTeams, // Alle Teams speichern
        dailyLeads,
        loading: false
      });
    };

    loadOrganizationStatistics();
  }, [isOrgLeader, isAdmin, user?.id, currentOrganizationId, dateRange]);

  // Handler für Team-Navigation
  const handleTeamClick = (teamData) => {
    if (teamData && teamData.id) {
      
      // Prüfe ob onNavigateToTeamDashboard Function verfügbar ist
      if (typeof onNavigateToTeamDashboard === 'function') {
        onNavigateToTeamDashboard(teamData.id, teamData.fullName);
      } else {
        console.warn('onNavigateToTeamDashboard function not available');
        // Fallback: Alert für den Benutzer
        alert(`Team-Dashboard für "${teamData.fullName}" würde hier geöffnet werden.`);
      }
    }
  };

  // Tooltip component removed - using card layout instead

  // Custom Tooltip für Daily Leads Chart
  const DailyLeadsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '8px 12px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          fontSize: '14px'
        }}>
          <p style={{ margin: '0', color: '#2d3748' }}>
            {label}: {payload[0].value} Leads
          </p>
        </div>
      );
    }
    return null;
  };

  // Prozentsatz berechnen
  const getPercentage = (value, total) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  // Top Performer Team finden
  const getTopPerformerTeam = () => {
    if (chartData.topPerformers.length > 0) {
      return chartData.topPerformers[0];
    }
    return null;
  };

  // Wenn kein Organisationsleiter oder Admin, zeige Meldung
  if (!isOrgLeader && !isAdmin) {
    return (
      <div className="team-dashboard-container">
        <div className="team-dashboard-header">
          <h2>Organisations Dashboard</h2>
        </div>
        <div className="no-team-data">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <p>Organisations Dashboard ist nur für Organisationsleiter und Administratoren verfügbar.</p>
          <span>Sie haben keine Berechtigung, Organisations-Statistiken einzusehen.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="team-dashboard-container">
      <div className="team-dashboard-header">
        <h2>Organisations Dashboard - Statistiken</h2>
        <span className="team-name">Meine Organisation</span>
      </div>
      
      {/* Date Range Filter */}
      <DateRangeFilter 
        onDateRangeChange={setDateRange}
        initialDays={14}
      />
      
      {orgStats.loading ? (
        <div className="stats-loading">
          <div className="loading-spinner"></div>
          <span>Lade Organisations-Statistiken...</span>
        </div>
      ) : orgStats.error ? (
        <div className="stats-error">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <span>{orgStats.error}</span>
        </div>
      ) : (
        <>
          <div className="team-stats-grid">
            {/* Gesamte Leads */}
            <div className="stat-card total-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.totalLeads}</div>
                <div className="stat-label">Leads eingegangen</div>
              </div>
            </div>
            
            {/* Offene Leads */}
            <div className="stat-card open-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 6v6l4 2"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.open}</div>
                <div className="stat-label">Offen</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.open, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Qualifizierte Leads */}
            <div className="stat-card qualified-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.qualified}</div>
                <div className="stat-label">Qualifiziert</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.qualified, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Kein Interesse */}
            <div className="stat-card no-interest-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.noInterest}</div>
                <div className="stat-label">Kein Interesse</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.noInterest, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Widerrufen */}
            <div className="stat-card revoked-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18l-2 13H5L3 6z"></path>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.revoked}</div>
                <div className="stat-label">Widerrufen</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.revoked, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Nicht qualifiziert */}
            <div className="stat-card not-qualified-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.notQualified}</div>
                <div className="stat-label">Nicht qualifiziert</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.notQualified, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* KWh zu niedrig */}
            <div className="stat-card kwh-too-low-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.kwhTooLow}</div>
                <div className="stat-label">KWh zu niedrig</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.kwhTooLow, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Denkmalschutz */}
            <div className="stat-card monument-protection-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.monumentProtection}</div>
                <div className="stat-label">Denkmalschutz</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.monumentProtection, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Dach muss saniert werden */}
            <div className="stat-card roof-renovation-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9,22 9,12 15,12 15,22"></polyline>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.roofNeedsRenovation}</div>
                <div className="stat-label">Dach saniert werden</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.roofNeedsRenovation, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Technisch nicht möglich */}
            <div className="stat-card technically-not-possible-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.technicallyNotPossible}</div>
                <div className="stat-label">Technisch nicht möglich</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.technicallyNotPossible, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
            
            {/* Dublette */}
            <div className="stat-card duplicate-leads">
              <div className="stat-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-number">{orgStats.duplicate}</div>
                <div className="stat-label">Dublette</div>
                <div className="stat-percentage">
                  {getPercentage(orgStats.duplicate, orgStats.totalLeads)}%
                </div>
              </div>
            </div>
          </div>

          {/* Chart-Sektion */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr',
            gap: '24px', 
            marginTop: '32px'
          }}>
            {/* Top Performer Chart (Teams) */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <h3 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '1.125rem', 
                fontWeight: '600', 
                color: '#2d3748' 
              }}>
                Team Performance Dashboard
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '400', 
                  color: '#718096', 
                  marginLeft: '8px' 
                }}>
                  (Klicken Sie auf ein Team)
                </span>
              </h3>
              {chartData.loading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#718096',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div className="loading-spinner"></div>
                  <span>Lade Chart-Daten...</span>
                </div>
              ) : chartData.topPerformers.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '16px',
                  marginTop: '20px'
                }}>
                  {getCurrentPageTeams().map((team, index) => (
                    <div
                      key={team.id}
                      onClick={() => handleTeamClick(team)}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        padding: '20px',
                        border: selectedTeamId === team.id ? '2px solid #3182ce' : '1px solid #e2e8f0',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '140px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      {/* Team Header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          color: '#2d3748',
                          margin: 0
                        }}>
                          {team.fullName}
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#718096',
                          fontWeight: '500'
                        }}>
                          #{index + 1 + (teamIndex * teamsPerPage)}
                        </div>
                      </div>

                      {/* Performance Stats */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                        marginBottom: '16px'
                      }}>
                        <div style={{
                          textAlign: 'center',
                          padding: '8px',
                          background: '#f7fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#4a6de5',
                            marginBottom: '4px'
                          }}>
                            {team.Gesamt}
                          </div>
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#718096',
                            fontWeight: '500'
                          }}>
                            Gesamt Leads
                          </div>
                        </div>
                        <div style={{
                          textAlign: 'center',
                          padding: '8px',
                          background: '#f0fff4',
                          borderRadius: '8px',
                          border: '1px solid #c6f6d5'
                        }}>
                          <div style={{
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#38a169',
                            marginBottom: '4px'
                          }}>
                            {team.Qualifiziert}
                          </div>
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#718096',
                            fontWeight: '500'
                          }}>
                            Qualifiziert
                          </div>
                        </div>
                      </div>

                      {/* Performance Bar */}
                      <div style={{
                        marginTop: 'auto'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '4px'
                        }}>
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#718096',
                            fontWeight: '500'
                          }}>
                            Qualifizierungsrate
                          </span>
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#4a5568',
                            fontWeight: '600'
                          }}>
                            {team.Gesamt > 0 ? Math.round((team.Qualifiziert / team.Gesamt) * 100) : 0}%
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          background: '#e2e8f0',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${team.Gesamt > 0 ? (team.Qualifiziert / team.Gesamt) * 100 : 0}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #4a6de5, #3182ce)',
                            borderRadius: '3px',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>

                      {/* Click Indicator */}
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        fontSize: '0.75rem',
                        color: '#a0aec0',
                        opacity: 0.7
                      }}>
                        Klicken →
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#a0aec0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <path d="M9 9h6v6H9z"></path>
                  </svg>
                  <span>Keine Organisations-Daten verfügbar</span>
                </div>
              )}
              
              {/* Paginierung für Team Performance */}
              {chartData.topPerformers.length > teamsPerPage && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '20px',
                  padding: '16px',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <button
                    onClick={handlePreviousPage}
                    disabled={teamIndex === 0}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      background: teamIndex === 0 ? '#f7fafc' : 'white',
                      color: teamIndex === 0 ? '#a0aec0' : '#4a5568',
                      cursor: teamIndex === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (teamIndex > 0) {
                        e.target.style.background = '#f7fafc';
                        e.target.style.borderColor = '#cbd5e0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (teamIndex > 0) {
                        e.target.style.background = 'white';
                        e.target.style.borderColor = '#e2e8f0';
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15,18 9,12 15,6"></polyline>
                    </svg>
                    Zurück
                  </button>
                  
                  <span style={{
                    fontSize: '14px',
                    color: '#718096',
                    minWidth: '60px',
                    textAlign: 'center'
                  }}>
                    {teamIndex + 1} / {getTotalPages()}
                  </span>
                  
                  <button
                    onClick={handleNextPage}
                    disabled={teamIndex >= getTotalPages() - 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      background: teamIndex >= getTotalPages() - 1 ? '#f7fafc' : 'white',
                      color: teamIndex >= getTotalPages() - 1 ? '#a0aec0' : '#4a5568',
                      cursor: teamIndex >= getTotalPages() - 1 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (teamIndex < getTotalPages() - 1) {
                        e.target.style.background = '#f7fafc';
                        e.target.style.borderColor = '#cbd5e0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (teamIndex < getTotalPages() - 1) {
                        e.target.style.background = 'white';
                        e.target.style.borderColor = '#e2e8f0';
                      }
                    }}
                  >
                    Weiter
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9,18 15,12 9,6"></polyline>
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Daily Leads Chart */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <h3 style={{ 
                margin: '0 0 20px 0', 
                fontSize: '1.125rem', 
                fontWeight: '600', 
                color: '#2d3748' 
              }}>
                Leads pro Tag (Alle Teams)
              </h3>
              {chartData.loading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#718096',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div className="loading-spinner"></div>
                  <span>Lade Chart-Daten...</span>
                </div>
              ) : chartData.dailyLeads.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart 
                    data={chartData.dailyLeads} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: '#4a5568' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#4a5568' }}
                    />
                    <Tooltip content={<DailyLeadsTooltip />} />
                    <Line 
                      type="monotone"
                      dataKey="leads" 
                      stroke="#4a6de5" 
                      strokeWidth={3}
                      dot={{ fill: '#4a6de5', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#4a6de5', strokeWidth: 2 }}
                      name="Leads"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#a0aec0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <path d="M9 9h6v6H9z"></path>
                  </svg>
                  <span>Keine Daily-Daten verfügbar</span>
                </div>
              )}
            </div>
          </div>

          {/* Zusätzliche Informationen mit Top Performer Click */}
          {orgStats.totalLeads > 0 && (
            <div className="team-insights">
              <h3>Organisations-Insights</h3>
              <div className="insights-grid">
                <div className="insight-item">
                  <span className="insight-label">Erfolgsrate:</span>
                  <span className="insight-value qualified-text">
                    {getPercentage(orgStats.qualified, orgStats.totalLeads)}%
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Verlustrate:</span>
                  <span className="insight-value negative-text">
                    {getPercentage(orgStats.noInterest + orgStats.revoked + orgStats.notQualified, orgStats.totalLeads)}%
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Gesamt Leads:</span>
                  <span className="insight-value neutral-text">
                    {orgStats.totalLeads}
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Offene Leads:</span>
                  <span className="insight-value neutral-text">
                    {getPercentage(orgStats.open, orgStats.totalLeads)}%
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Technische Hürden:</span>
                  <span className="insight-value negative-text">
                    {getPercentage(orgStats.kwhTooLow + orgStats.monumentProtection + orgStats.roofNeedsRenovation + orgStats.technicallyNotPossible, orgStats.totalLeads)}%
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Dubletten:</span>
                  <span className="insight-value negative-text">
                    {getPercentage(orgStats.duplicate, orgStats.totalLeads)}%
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Top Performer Team:</span>
                  <span 
                    className="insight-value clickable-text"
                    style={{ 
                      cursor: 'pointer', 
                      color: '#4a6de5',
                      textDecoration: 'underline'
                    }}
                    onClick={() => {
                      const topTeam = getTopPerformerTeam();
                      if (topTeam) {
                        handleTeamClick(topTeam);
                      }
                    }}
                    title="Klicken für Team-Dashboard"
                  >
                    {getTopPerformerTeam()?.fullName || 'N/A'}
                  </span>
                </div>
                <div className="insight-item">
                  <span className="insight-label">Anzahl Teams:</span>
                  <span className="insight-value neutral-text">
                    {teams.length} Teams
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Kein Data Zustand */}
      {!orgStats.loading && !orgStats.error && orgStats.totalLeads === 0 && (
        <div className="no-team-data">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
            <line x1="9" y1="9" x2="9.01" y2="9"></line>
            <line x1="15" y1="9" x2="15.01" y2="9"></line>
          </svg>
          <p>Keine Leads für diese Organisation gefunden.</p>
          <span>Beginnen Sie mit der Lead-Generierung für Ihre Organisation!</span>
        </div>
      )}
    </div>
  );
}

export default OrganizationDashboard;